import React, { useEffect, useState } from 'react';
import { auth } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db } from '../../firebase';
import { doc, setDoc, collection, deleteDoc, onSnapshot } from 'firebase/firestore';
import './AddServerForm.css';
import { 
  FaUserPlus, FaUsers, FaUser, FaEnvelope, FaLock, FaEdit, FaTrash, 
  FaCheck, FaTimes, FaExclamationTriangle, FaCheckCircle, FaDownload,
  FaSpinner, FaIdCard, FaUserTag
} from 'react-icons/fa';

const AddServerForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editingServer, setEditingServer] = useState(null);

  useEffect(() => {
    const serversCollection = collection(db, 'identifications');
    
    const unsubscribe = onSnapshot(serversCollection, (snapshot) => {
      const serverList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setServers(serverList);
    }, (error) => {
      console.error("Erreur lors de la récupération des serveurs : ", error);
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (selectedServer) {
        // Mise à jour uniquement du nom et du prénom
        const serverDoc = doc(db, 'identifications', selectedServer.id);
        await setDoc(serverDoc, {
          ...selectedServer, // Conserver les données existantes
          firstName,
          lastName,
        });

        setSuccess('Nom et prénom mis à jour avec succès !');
      } else {
        // Ajouter un nouveau serveur
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        await setDoc(doc(db, 'identifications', user.uid), {
          email: user.email,
          firstName,
          lastName,
          role: 'server',
          createdAt: new Date(),
        });
        setSuccess('Serveur ajouté avec succès !');
      }

      // Réinitialiser les champs
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setSelectedServer(null);
    } catch (error) {
      setError('Erreur lors de l\'ajout ou de la mise à jour du serveur : ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (server) => {
    setEditingServer(server);
    setShowEditPopup(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const serverDoc = doc(db, 'identifications', editingServer.id);
      await setDoc(serverDoc, {
        ...editingServer,
        firstName: editingServer.firstName,
        lastName: editingServer.lastName,
      });

      setSuccess('Serveur mis à jour avec succès !');
      setShowEditPopup(false);
      setEditingServer(null);
    } catch (error) {
      setError('Erreur lors de la mise à jour du serveur : ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (serverId) => {
    try {
      // Appeler le backend pour supprimer l'utilisateur dans Firebase Authentication
      const response = await fetch('https://skaneat-app.vercel.app/delete-user', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uid: serverId }),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la suppression de l’utilisateur.');
      }
  
      await deleteDoc(doc(db, 'identifications', serverId));
      await deleteDoc(doc(db, 'horaires', serverId));
  
      setSuccess('Serveur supprimé avec succès !');
    } catch (error) {
      setError('Erreur lors de la suppression du serveur : ' + error.message);
    }
  };  

  const exportToCSV = () => {
    const today = new Date().toLocaleDateString('fr-FR');
    const headers = ['Nom', 'Prenom', 'Email', 'Role', 'Date de creation'];
    
    // Formater les données
    const csvData = servers
      .filter((server) => server.role === 'server')
      .map((server) => {
        const createdAt = server.createdAt?.toDate 
          ? server.createdAt.toDate().toLocaleDateString('fr-FR')
          : 'Non specifie';
        
        return [
          server.firstName,
          server.lastName,
          server.email,
          server.role,
          createdAt
        ].join(';');
      });
    
    // Assembler le contenu CSV
    const csvContent = [
      headers.join(';'),
      ...csvData
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `serveurs_${today.replace(/\//g, '-')}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="server-management-container">
      {/* Header */}
      <div className="server-management-header">
        <h1 className="server-management-title">
          <FaUsers className="server-title-icon" />
          Gestion des Serveurs
        </h1>
        <p className="server-management-subtitle">
          Ajoutez et gérez les comptes serveurs de votre restaurant
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="server-message server-error-message">
          <FaExclamationTriangle className="server-message-icon" />
          {error}
        </div>
      )}
      {success && (
        <div className="server-message server-success-message">
          <FaCheckCircle className="server-message-icon" />
          {success}
        </div>
      )}

      {/* Form Section */}
      <div className="server-form-section">
        <h2 className="server-form-title">
          <FaUserPlus className="server-form-icon" />
          Ajouter un serveur
        </h2>
        
        <form className="server-form" onSubmit={handleSubmit}>
          <div className="server-form-row">
            <div className="server-form-group">
              <label className="server-form-label">
                <FaUser className="server-label-icon" />
                Nom
              </label>
              <input
                type="text"
                className="server-form-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Ex: Dupont"
                required
              />
            </div>
            <div className="server-form-group">
              <label className="server-form-label">
                <FaIdCard className="server-label-icon" />
                Prénom
              </label>
              <input
                type="text"
                className="server-form-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Ex: Jean"
                required
              />
            </div>
          </div>

          <div className="server-form-row">
            <div className="server-form-group">
              <label className="server-form-label">
                <FaEnvelope className="server-label-icon" />
                Email du serveur
              </label>
              <input
                type="email"
                className="server-form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Ex: jean.dupont@restaurant.com"
                required
              />
            </div>
            <div className="server-form-group">
              <label className="server-form-label">
                <FaLock className="server-label-icon" />
                Mot de passe du serveur
              </label>
              <input
                type="password"
                className="server-form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe sécurisé"
                required
              />
            </div>
          </div>

          <div className="server-form-actions">
            <button 
              type="submit" 
              className="server-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <FaSpinner className="spinning" />
                  Ajout en cours...
                </>
              ) : (
                <>
                  <FaCheck />
                  Ajouter un serveur
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Server List Section */}
      <div className="server-list-section">
        <div className="server-list-header">
          <h3 className="server-list-title">
            <FaUserTag className="server-list-icon" />
            Serveurs Enregistrés
          </h3>
          <button onClick={exportToCSV} className="server-export-btn">
            <FaDownload />
            Exporter en CSV
          </button>
        </div>
        
        {servers.filter((server) => server.role === 'server').length === 0 ? (
          <div className="server-empty-state">
            <FaUsers className="server-empty-icon" />
            <p className="server-empty-text">Aucun serveur enregistré pour le moment</p>
          </div>
        ) : (
          <ul className="server-list">
            {servers
              .filter((server) => server.role === 'server')
              .map((server) => (
                <li key={server.id} className="server-list-item">
                  <div className="server-info">
                    <div className="server-name">
                      {server.firstName} {server.lastName}
                    </div>
                    <div className="server-email">
                      {server.email}
                    </div>
                    <div className="server-role-badge">
                      Serveur
                    </div>
                  </div>
                  <div className="server-actions">
                    <button 
                      className="server-action-btn server-edit-btn"
                      onClick={() => handleEdit(server)}
                    >
                      <FaEdit />
                      Modifier
                    </button>
                    <button 
                      className="server-action-btn server-delete-btn"
                      onClick={() => handleDelete(server.id)}
                    >
                      <FaTrash />
                      Supprimer
                    </button>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </div>

      {/* Edit Server Popup */}
      {showEditPopup && editingServer && (
        <div className="server-popup-overlay" onClick={() => setShowEditPopup(false)}>
          <div className="server-popup" onClick={e => e.stopPropagation()}>
            <div className="server-popup-header">
              <h3 className="server-popup-title">
                <FaEdit className="server-popup-icon" />
                Modifier le serveur
              </h3>
            </div>
            
            <div className="server-popup-body">
              <form className="server-form" onSubmit={handleEditSubmit}>
                <div className="server-form-row">
                  <div className="server-form-group">
                    <label className="server-form-label">
                      <FaUser className="server-label-icon" />
                      Nom
                    </label>
                    <input
                      type="text"
                      className="server-form-input"
                      value={editingServer.firstName || ''}
                      onChange={(e) => setEditingServer({
                        ...editingServer,
                        firstName: e.target.value
                      })}
                      placeholder="Ex: Dupont"
                      required
                    />
                  </div>
                  <div className="server-form-group">
                    <label className="server-form-label">
                      <FaIdCard className="server-label-icon" />
                      Prénom
                    </label>
                    <input
                      type="text"
                      className="server-form-input"
                      value={editingServer.lastName || ''}
                      onChange={(e) => setEditingServer({
                        ...editingServer,
                        lastName: e.target.value
                      })}
                      placeholder="Ex: Jean"
                      required
                    />
                  </div>
                </div>

                <div className="server-form-group">
                  <label className="server-form-label">
                    <FaEnvelope className="server-label-icon" />
                    Email
                  </label>
                  <input
                    type="email"
                    className="server-form-input"
                    value={editingServer.email || ''}
                    disabled
                    style={{ background: 'var(--server-bg-color)', color: 'var(--server-text-secondary)' }}
                  />
                  <small style={{ color: 'var(--server-text-secondary)', fontSize: '0.75rem' }}>
                    L'email ne peut pas être modifié
                  </small>
                </div>
              </form>
            </div>

            <div className="server-popup-actions">
              <button 
                className="server-popup-btn primary" 
                onClick={handleEditSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FaSpinner className="spinning" />
                    Mise à jour...
                  </>
                ) : (
                  <>
                    <FaCheck />
                    Mettre à jour
                  </>
                )}
              </button>
              <button 
                className="server-popup-btn secondary" 
                onClick={() => {
                  setShowEditPopup(false);
                  setEditingServer(null);
                }}
              >
                <FaTimes />
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddServerForm;
